import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import FinLogo from "../../../img/fin.png";
import { fetchOrderById, fetchOrderItemsByOrderId } from "../../../redux-toolkit/order/order-thunks";
import {
  selectOrder,
  selectOrderItems,
  selectIsOrderLoading,
} from "../../../redux-toolkit/order/order-selector";
import Spinner from "../../../components/Spinner/Spinner";
import "./InvoicePage.css";

const COMPANY = {
  name:    "Finllie",
  gstin:   "29AKEPR4195D1ZV",
  address: "No 44, 45, 46, Silicon Town Main Road, Doddanagamangala, Electronic City Phase 2, Bengaluru, Karnataka 560100, India",
  email:   "finlliesales@gmail.com",
  phone:   "+91 9945089397",
};

const InvoicePage = () => {
  const dispatch = useDispatch();
  const history  = useHistory();
  const params   = useParams<{ id: string }>();

  const order   = useSelector(selectOrder);
  const items   = useSelector(selectOrderItems);
  const loading = useSelector(selectIsOrderLoading);

  useEffect(() => {
    dispatch(fetchOrderById(params.id));
    dispatch(fetchOrderItemsByOrderId(params.id));
  }, [dispatch, params.id]);

  const handlePrint = () => window.print();

  const subtotal = items.reduce((s, item) => s + Number(item.amount || 0), 0);
  const grandTotal = order.totalPrice != null ? Number(order.totalPrice) : subtotal;
  const gstAmt = Math.round(subtotal * 0.18);

  if (loading) return <Spinner />;

  return (
    <div className="inv-shell">

      {/* ── ACTION BAR (hidden on print) ── */}
      <div className="inv-action-bar no-print">
        <button className="inv-back-btn" onClick={() => history.goBack()}>
          ← Back
        </button>
        <div className="inv-action-right">
          <span className="inv-action-hint">Invoice #{order.id}</span>
          <button className="inv-print-btn" onClick={handlePrint}>
            🖨 Download / Print PDF
          </button>
        </div>
      </div>

      {/* ── INVOICE DOCUMENT ── */}
      <div className="inv-doc" id="invoice-doc">

        {/* ── TOP HEADER ── */}
        <div className="inv-top">
          <div className="inv-brand">
            <img src={FinLogo} alt="Finllie" className="inv-brand-logo" />
            <div className="inv-brand-info">
              <div className="inv-brand-name">{COMPANY.name}</div>
              <div className="inv-brand-sub">{COMPANY.address}</div>
              <div className="inv-brand-sub">GSTIN: <strong>{COMPANY.gstin}</strong></div>
              <div className="inv-brand-sub">{COMPANY.email} · {COMPANY.phone}</div>
            </div>
          </div>

          <div className="inv-title-block">
            <div className="inv-title-text">TAX INVOICE</div>
            <div className="inv-meta-grid">
              <span className="inv-meta-label">Invoice No.</span>
              <span className="inv-meta-val">INV-{String(order.id).padStart(6, "0")}</span>
              <span className="inv-meta-label">Order ID</span>
              <span className="inv-meta-val">#{order.id}</span>
              <span className="inv-meta-label">Date</span>
              <span className="inv-meta-val">
                {order.createdAt
                  ? new Date(order.createdAt).toLocaleString("en-IN", {
                      timeZone: "Asia/Kolkata",
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    })
                  : order.date
                    ? new Date(order.date).toLocaleDateString("en-IN", {
                        timeZone: "Asia/Kolkata",
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                    : "—"}
              </span>
              <span className="inv-meta-label">Payment</span>
              <span className="inv-meta-val">{order.paymentType ?? "—"}</span>
            </div>
          </div>
        </div>

        {/* ── DIVIDER ── */}
        <div className="inv-rule" />

        {/* ── BILL TO / SHIP TO ── */}
        <div className="inv-parties">
          <div className="inv-party">
            <div className="inv-party-title">BILL TO</div>
            <div className="inv-party-name">
              {[order.firstName, order.lastName].filter(Boolean).join(" ") || "—"}
            </div>
            <div className="inv-party-line">{order.email}</div>
            <div className="inv-party-line">{order.phoneNumber}</div>
            {order.transactionId && (
              <div className="inv-party-line">Txn: {order.transactionId}</div>
            )}
          </div>

          <div className="inv-party">
            <div className="inv-party-title">SHIP TO</div>
            <div className="inv-party-name">
              {[order.firstName, order.lastName].filter(Boolean).join(" ") || "—"}
            </div>
            <div className="inv-party-line">{order.address || "—"}</div>
            <div className="inv-party-line">
              {[order.city, order.postIndex].filter(Boolean).join(" – ") || "—"}
            </div>
          </div>

          <div className="inv-party inv-party-status">
            <div className="inv-party-title">STATUS</div>
            <div className="inv-status-badge">{order.deliveryStatus ?? "—"}</div>
            {order.orderType && (
              <div className="inv-order-type">{order.orderType}</div>
            )}
          </div>
        </div>

        <div className="inv-rule" />

        {/* ── ITEMS TABLE ── */}
        <table className="inv-table">
          <thead>
            <tr>
              <th className="inv-th inv-th-num">#</th>
              <th className="inv-th">Item Description</th>
              <th className="inv-th">Brand</th>
              <th className="inv-th inv-th-right">Unit Price (₹)</th>
              <th className="inv-th inv-th-right">Qty</th>
              <th className="inv-th inv-th-right">Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={item.id} className={index % 2 === 0 ? "inv-tr-even" : ""}>
                <td className="inv-td inv-td-num">{index + 1}</td>
                <td className="inv-td inv-td-name">{item.perfume?.perfumeTitle ?? "—"}</td>
                <td className="inv-td">{item.perfume?.brand ?? "—"}</td>
                <td className="inv-td inv-td-right">
                  {Number(item.perfume?.price ?? 0).toFixed(2)}
                </td>
                <td className="inv-td inv-td-right">{item.quantity}</td>
                <td className="inv-td inv-td-right inv-td-amount">
                  {Number(item.amount ?? 0).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* ── TOTALS ── */}
        <div className="inv-totals-wrap">
          <div className="inv-totals">
            <div className="inv-total-row">
              <span>Subtotal</span>
              <span>₹{subtotal.toFixed(2)}</span>
            </div>
            <div className="inv-total-row">
              <span>GST (18%)</span>
              <span>₹{gstAmt.toFixed(2)}</span>
            </div>
            <div className="inv-total-row">
              <span>Shipping</span>
              <span className="inv-free">FREE</span>
            </div>
            <div className="inv-rule inv-rule-sm" />
            <div className="inv-total-row inv-grand">
              <span>Grand Total</span>
              <span>₹{grandTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* ── FOOTER ── */}
        <div className="inv-rule" />
        <div className="inv-footer">
          <div className="inv-footer-left">
            <div className="inv-footer-note">
              This is a computer-generated invoice and does not require a physical signature.
            </div>
            <div className="inv-footer-note">
              For queries: {COMPANY.email}
            </div>
          </div>

        </div>

        <div className="inv-thankyou">
          Thank you for your business with Finllie! 🎉
        </div>
      </div>
    </div>
  );
};

export default InvoicePage;